import { lazy, Suspense, Component, type ReactNode } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import ScrollToTop from "@/components/ScrollToTop";
import { LanguageProvider } from "@/hooks/useLanguage";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { LANGUAGES } from "@/lib/routes";
import { routePaths } from "@/lib/routes";
import { lazyRoute } from "@/lib/lazyRoute";

const FloatingCTA = lazyRoute(() => import("@/components/mobile/FloatingCTA"));
import { SelectionProvider } from "@/features/selection/SelectionContext";
const SelectionLayer = lazyRoute(() => import("@/features/selection/SelectionLayer").then((m) => ({ default: m.SelectionLayer })));

/** Legacy redirect for /produkte/:type/:category → /de/produkte/:type/:category */
const ProdukteLegacyRedirect = () => {
  const { type, category } = useParams();
  return <Navigate to={`/de/produkte/${type}/${category}`} replace />;
};

/** Legacy redirect for /hersteller/:slug → /de/hersteller/:slug */
const HerstellerLegacyRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/de/hersteller/${slug}`} replace />;
};

const TiltInitializer = lazyRoute(() => import("@/components/TiltInitializer"));
const AtmosphereLayer = lazyRoute(() => import("@/components/atmosphere/AtmosphereLayer"));
const LightCanvas = lazyRoute(() => import("@/components/canvas/LightCanvas"));
const RouteTransition = lazyRoute(() => import("@/components/motion/RouteTransition"));
import ShowroomAtmosphere from "@/components/atmosphere/ShowroomAtmosphere";
import GalleryEntrance from "@/components/GalleryEntrance";
import ScrollFocusEngine from "@/components/atmosphere/ScrollFocusEngine";
import VisualViewportBridge from "@/components/VisualViewportBridge";

const HomePage = lazyRoute(() => import("./pages/HomePage"));
const AboutPage = lazyRoute(() => import("./pages/AboutPage"));
const ProductsPage = lazyRoute(() => import("./pages/ProductsPage"));
const ContactPage = lazyRoute(() => import("./pages/ContactPage"));
const ManufacturersPage = lazyRoute(() => import("./pages/ManufacturersPage"));
const ManufacturerDetailPage = lazyRoute(() => import("./pages/ManufacturerDetailPage"));
const CategoryPage = lazyRoute(() => import("./pages/CategoryPage"));
const NotFound = lazyRoute(() => import("./pages/NotFound"));
const ImpressumPage = lazyRoute(() => import("./pages/ImpressumPage"));
const DatenschutzPage = lazyRoute(() => import("./pages/DatenschutzPage"));
const CookiePolicyPage = lazyRoute(() => import("./pages/CookiePolicyPage"));
const TermsPage = lazyRoute(() => import("./pages/TermsPage"));
const AdminBbfUploadPage = lazyRoute(() => import("./pages/AdminBbfUploadPage"));
const AdminImageAuditPage = lazyRoute(() => import("./pages/AdminImageAuditPage"));
const NewsArchivePage = lazyRoute(() => import("./pages/NewsArchivePage"));
const NewsArticlePage = lazyRoute(() => import("./pages/NewsArticlePage"));
const B2BLeadConversionPage = lazyRoute(() => import("./pages/B2BLeadConversionPage"));

/** Catches chunk-load failures so they don't blank the entire app */
class ChunkErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  render() { return this.state.hasError ? null : this.props.children; }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <BrowserRouter>
        <LanguageProvider>
          <SelectionProvider>
          <Toaster />
          <Sonner />

          {/* Global cinematic gallery atmosphere — fixed, behind everything. */}
          <ShowroomAtmosphere />
          <ScrollFocusEngine />
          <VisualViewportBridge />

          {/* Experience Layer — wrapped in error boundary so chunk-load failures don't blank the app */}
          <ChunkErrorBoundary>
            <Suspense fallback={null}>
              <LightCanvas />
              <AtmosphereLayer />
              <TiltInitializer />
            </Suspense>
          </ChunkErrorBoundary>

          <ScrollToTop />
          <Suspense fallback={null}>
            <RouteTransition />
          </Suspense>
          <Navbar />

          <main className="relative z-10 min-h-screen overflow-visible bg-background">
            <Suspense fallback={null}>
              <Routes>
                {/* Root redirects to detected language (handled by LanguageProvider) */}
                <Route path="/" element={null} />

                {/* Generate routes for each language */}
                {LANGUAGES.map((lang) => (
                  <Route key={lang} path={`/${lang}`}>
                    <Route index element={<HomePage />} />
                    <Route path={routePaths.about[lang]} element={<AboutPage />} />
                    <Route path={routePaths.products[lang]} element={<ProductsPage />} />
                    <Route path={routePaths.contact[lang]} element={<ContactPage />} />
                    <Route path={routePaths.manufacturers[lang]} element={<ManufacturersPage />} />
                    <Route path={`${routePaths.manufacturers[lang]}/:slug`} element={<ManufacturerDetailPage />} />
                    <Route path={`${routePaths.productsPrefix[lang]}/:type/:category`} element={<CategoryPage />} />
                    <Route path={routePaths.impressum[lang]} element={<ImpressumPage />} />
                    <Route path={routePaths.datenschutz[lang]} element={<DatenschutzPage />} />
                    <Route path={routePaths.cookiePolicy[lang]} element={<CookiePolicyPage />} />
                    <Route path={routePaths.terms[lang]} element={<TermsPage />} />
                    <Route path={routePaths.news[lang]} element={<NewsArchivePage />} />
                    <Route path={`${routePaths.news[lang]}/:slug`} element={<NewsArticlePage />} />
                  </Route>
                ))}

                {/* Legacy routes → redirect to German */}
                <Route path="/about" element={<Navigate to="/de/agentur" replace />} />
                <Route path="/products" element={<Navigate to="/de/sortiment" replace />} />
                <Route path="/contact" element={<Navigate to="/de/kontakt" replace />} />
                <Route path="/hersteller" element={<Navigate to="/de/hersteller" replace />} />
                <Route path="/hersteller/:slug" element={<HerstellerLegacyRedirect />} />
                <Route path="/impressum" element={<Navigate to="/de/impressum" replace />} />
                <Route path="/datenschutz" element={<Navigate to="/de/datenschutz" replace />} />
                <Route path="/cookie-richtlinie" element={<Navigate to="/de/cookie-richtlinie" replace />} />
                <Route path="/nutzungsbedingungen" element={<Navigate to="/de/nutzungsbedingungen" replace />} />
                <Route path="/produkte/:type/:category" element={<ProdukteLegacyRedirect />} />

                <Route path="/admin/bbf-upload" element={<AdminBbfUploadPage />} />
                <Route path="/admin/image-audit" element={<AdminImageAuditPage />} />
                <Route path="/b2b-lead-conversion" element={<B2BLeadConversionPage />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </main>

          <Footer />
          <Suspense fallback={null}>
            <FloatingCTA />
            <SelectionLayer />
          </Suspense>

          {/* Premium gallery entrance — once per session, home root only */}
          <GalleryEntrance />
          </SelectionProvider>
        </LanguageProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
